import type { ReactNode } from 'react';
import { clsx } from 'clsx';

import { Spinner } from '@/components/common/Spinner';
import { SvgEyeClosed, SvgEyeOpen, SvgHelp, SvgRefresh } from '@/components/icons';
import { useAppStore, useUiStore } from '@/store/app';

/**
 * The 36px strip Actual reserves at the top of the content area (see
 * `styles.page` paddingTop and `Titlebar` in FinancesApp). In Electron it also
 * hosts the window drag region; here it carries the same right-side controls.
 */
export function Titlebar() {
  const hideAmounts = useUiStore(state => state.hideAmounts);
  const toggleHideAmounts = useUiStore(state => state.toggleHideAmounts);
  const syncing = useAppStore(state => state.syncing);
  const runSync = useAppStore(state => state.runSync);

  return (
    <div className="flex h-9 shrink-0 items-center justify-end gap-4 px-3 text-sm text-page-text-light small:gap-5">
      <button
        type="button"
        onClick={toggleHideAmounts}
        title={hideAmounts ? '显示金额' : '隐藏金额'}
        data-testid="toggle-hide-amounts"
        className="rounded-sm p-1 hover:bg-button-bare-background-hover hover:text-page-text"
      >
        {hideAmounts ? (
          <SvgEyeClosed width={16} height={16} />
        ) : (
          <SvgEyeOpen width={16} height={16} />
        )}
      </button>

      <button
        type="button"
        onClick={() => void runSync()}
        data-testid="sync-button"
        className="flex items-center gap-1.5 hover:text-page-text"
      >
        {syncing ? (
          <Spinner width={14} />
        ) : (
          <SvgRefresh width={14} height={14} />
        )}
        同步
      </button>

      <span className="hidden items-center gap-1.5 small:flex" data-testid="server-status">
        <span className="h-2 w-2 rounded-full bg-sidebar-item-background-positive" />
        服务器在线
      </span>

      <a
        href="https://actualbudget.org/docs/"
        target="_blank"
        rel="noreferrer"
        className="flex items-center gap-1.5 hover:text-page-text"
      >
        <SvgHelp width={15} height={15} />
        帮助
      </a>
    </div>
  );
}

type PageProps = {
  title?: ReactNode;
  headerRight?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
};

/** Port of `components/Page.tsx` + `PageHeader`. */
export function Page({
  title,
  headerRight,
  children,
  className,
  contentClassName,
}: PageProps) {
  return (
    <div className={clsx('flex min-h-0 flex-1 flex-col', className)}>
      <div className="flex shrink-0 items-start justify-between pr-5 pl-5">
        {title && (
          <h1 className="ml-1 select-none pt-1 text-[25px] font-medium text-page-text">
            {title}
          </h1>
        )}
        {headerRight}
      </div>
      <div
        className={clsx(
          'flex min-h-0 flex-1 flex-col px-0.5 small:px-5',
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>
  );
}
